import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ChevronLeft,
  ChevronRight,
  CircleNotifications,
  Folder,
  FolderOpen,
} from '@mui/icons-material';
import {
  Box,
  Chip,
  debounce,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { addMonths, format, parseISO, startOfMonth, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_ColumnOrderState,
  MRT_Row,
  MRT_RowVirtualizer,
  MRT_ShowHideColumnsButton,
  MRT_SortingState,
  MRT_ToggleFullScreenButton,
  MRT_VisibilityState,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import { useQuery } from 'react-query';
import { Link, useHistory } from 'react-router-dom';
import { useMonitorContext } from 'src/pages/Monitor/MonitorContextProvider';
import { getWalletTableColumnsPreferences } from 'src/services/wallet-monitor';
import themev5 from 'src/themev5';
import { Operation } from 'src/types/operation';
import {
  FicCompositionStatus,
  FlattenedTableRow,
  IndicatorsResponse,
} from 'src/types/wallet';
import { TableColumnsPreference } from 'src/types/wallet-monitor';

import { FollowedFundActionsMenu } from './FollowedFundActionsMenu';
import ToolbarFundIndicatorsTable from './Toolbar';
import {
  formatFicMarketValueInMillions,
  formatFicPlPercentage,
  resolveFicPlPercentage,
} from './ficPl';
import { buildIndicatorsTableRow } from './utils';
import { IndicatorCellItem } from '../IndicatorCellItem';
import { SaveTableColumnsPreferencesModal } from '../SaveTableColumnsPreferencesModal';

function resolveOperationNavigationId(slug: string, hashId: string): string {
  return slug.match(/-(\d+)$/)?.[1] || hashId;
}

type DynamicIndicatorsTableProps = {
  walletId: number;
  market?: string;
  viewMode?: 'wallet' | 'operation';
  columnOrder: MRT_ColumnOrderState;
  setColumnOrder: Dispatch<SetStateAction<MRT_ColumnOrderState>>;
  columnVisibility: MRT_VisibilityState;
  setColumnVisibility: Dispatch<SetStateAction<MRT_VisibilityState>>;
  isLoadingIndicators: boolean;
  indicatorsResponseData: IndicatorsResponse[];
  isLoadingColumns?: boolean;
  columnsData: {
    data: MRT_ColumnDef<any>[];
  };
  valueType: 'percentage' | 'amount';
  handleIndicatorClick: (
    operation: Operation,
    indicatorName: string,
    indicatorColumn: string,
  ) => void;
  ownerId?: string;
  competenceDate?: string;
  enableFollowActions?: boolean;
  isFollowedFunds?: boolean;
};

const percentageColumnsOrdered = [
  'delta_dc_pct',
  'delta_atrasos_pct',
  'pct_atraso_sobre_dc',
  'delta_pdd_pct',
  'pdd_sobre_dc',
  'pct_pdd_sobre_atrasos',
  'delta_recompras_pct',
  'recompra_sobre_dc',
  'delta_substituicoes_pct',
  'amort_pct',
  'pct_caixa_sobre_pl',
];

const ficCompositionStatusLabels: Record<
  Extract<FicCompositionStatus, 'added' | 'removed'>,
  string
> = {
  added: 'Adicionado',
  removed: 'Removido',
};

export function DynamicIndicatorsTable({
  columnOrder,
  setColumnOrder,
  columnVisibility,
  setColumnVisibility,
  isLoadingIndicators,
  isLoadingColumns,
  indicatorsResponseData,
  columnsData,
  valueType,
  walletId,
  viewMode = 'wallet',
  handleIndicatorClick,
  ownerId,
  competenceDate,
  enableFollowActions = false,
  isFollowedFunds = false,
}: DynamicIndicatorsTableProps) {
  const isOperationView = viewMode === 'operation';
  const history = useHistory();
  const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [expanded, setExpanded] = useState<true | Record<string, boolean>>({});
  const hasAppliedDefaultExpansionRef = useRef(false);
  const {
    indicatorsCompetencyDate,
    setIndicatorsCompetencyDate,
    handleIndicatorsCompetencyDateChange,
    wallet,
  } = useMonitorContext();
  const [localCompetence, setLocalCompetence] = useState(
    indicatorsCompetencyDate,
  );
  const competence = isFollowedFunds
    ? indicatorsCompetencyDate
    : localCompetence;
  const [openSavePreferencesModal, setOpenSavePreferencesModal] =
    useState(false);
  const [hasLoadedPreference, setHasLoadedPreference] = useState(false);
  const [selectedTableColumnsPreference, setSelectedTableColumnsPreference] =
    useState<TableColumnsPreference | null>(null);

  const { data: walletPreferenceData } = useQuery(
    ['wallet-table-preference', walletId],
    () => getWalletTableColumnsPreferences(walletId),
    {
      enabled: !!walletId && !isLoadingColumns && !isOperationView,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 60,
    },
  );

  const filteredColumns = useMemo(() => {
    return (
      columnsData?.data?.filter(
        (column: MRT_ColumnDef<any>) => column.meta === valueType,
      ) || []
    );
  }, [columnsData?.data, valueType]);

  const flattenedData = useMemo(
    (): FlattenedTableRow[] =>
      indicatorsResponseData?.map((item: IndicatorsResponse) =>
        buildIndicatorsTableRow(item, false, isFollowedFunds),
      ) ?? [],
    [indicatorsResponseData, isFollowedFunds],
  );

  const hasExpandableRows = useMemo(
    () => flattenedData.some((row) => Boolean(row.subRows?.length)),
    [flattenedData],
  );

  // FICs devem vir expandidos por padrão nas visualizações de carteira e operação.
  // Aplicado apenas uma vez para não sobrescrever colapsos manuais do usuário.
  useEffect(() => {
    if (hasAppliedDefaultExpansionRef.current) return;
    if (!hasExpandableRows) return;

    setExpanded(true);
    hasAppliedDefaultExpansionRef.current = true;
  }, [isOperationView, hasExpandableRows]);

  const totalChildRowCount = useMemo(
    () =>
      flattenedData.reduce(
        (count, row) => count + (row.subRows?.length ?? 0),
        0,
      ),
    [flattenedData],
  );

  const enableRowVirtualization = totalChildRowCount > 50;

  const handleAlertClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, walletFundId: number) => {
      event.preventDefault();
      event.stopPropagation();

      const alertLink = ownerId
        ? `/monitor/${ownerId}/wallet/${walletId}/fund/${walletFundId}/alerts`
        : `/monitor/wallet/${walletId}/fund/${walletFundId}/alerts`;
      history.push(alertLink);
    },
    [history, ownerId, walletId],
  );

  const alertColumn: MRT_ColumnDef<any> = useMemo(
    () => ({
      accessorKey: 'alerta',
      header: 'Alerta',
      size: 50,
      minSize: 40,
      maxSize: 100,
      enableSorting: false,
      enableColumnFilter: false,
      enableColumnActions: false,
      enableResizing: false,
      enableHiding: false,
      enableColumnDragging: false,
      muiTableHeadCellProps: {
        sx: {
          textOverflow: 'unset !important',
          overflow: 'visible !important',
          whiteSpace: 'nowrap',
        },
      },
      Cell: ({ row }: { row: MRT_Row<any> }) => {
        const walletFundId = row.original.walletFundId;
        const isReferenceOnly =
          row.original.isFicCompositionReferenceOnly === true;

        // Fundos não cadastrados chegam com walletFundId 0 e não possuem
        // página de alertas — navegar levaria a um redirect de erro.
        if (!walletFundId || isReferenceOnly) {
          return null;
        }

        return (
          <Tooltip title="Alertas do fundo">
            <Box
              onClick={(event) => handleAlertClick(event, walletFundId)}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
                '&:hover': { cursor: 'pointer' },
              }}>
              <CircleNotifications fontSize="large" color="primary" />
            </Box>
          </Tooltip>
        );
      },
    }),
    [handleAlertClick],
  );

  const followedFundActionsColumn: MRT_ColumnDef<any> = useMemo(
    () => ({
      accessorKey: 'followedFundActions',
      header: 'Ações',
      size: 70,
      minSize: 60,
      maxSize: 90,
      enableSorting: false,
      enableColumnFilter: false,
      enableColumnActions: false,
      enableResizing: false,
      enableHiding: false,
      enableColumnDragging: false,
      Cell: ({ row }: { row: MRT_Row<any> }) => {
        const isReferenceOnly =
          row.original.isFicCompositionReferenceOnly === true;

        if (
          row.depth !== 0 ||
          !row.original.operation.hashId ||
          isReferenceOnly
        ) {
          return null;
        }

        return (
          <FollowedFundActionsMenu
            operationId={row.original.operation.hashId}
            competenceDate={
              isOperationView && competenceDate ? competenceDate : competence
            }
          />
        );
      },
    }),
    [walletId, isOperationView, competenceDate, competence],
  );

  const operationColumn: MRT_ColumnDef<any> = useMemo(
    () => ({
      accessorKey: 'operationName',
      header: 'Operação',
      size: isFollowedFunds ? 340 : undefined,
      minSize: isFollowedFunds ? 280 : 230,
      maxSize: isFollowedFunds ? 500 : 350,
      enableSorting: true,
      enableHiding: false,
      enableColumnDragging: false,
      muiTableHeadCellProps: {
        align: 'left',
        sx: {
          pl: 2,
          pr: 1,
          textOverflow: 'unset !important',
          overflow: 'visible !important',
          whiteSpace: 'nowrap',
        },
      },
      muiTableBodyCellProps: {
        align: 'left',
        sx: {
          pl: 2,
          pr: 1,
          py: 1,
        },
      },
      Cell: ({ cell, row }: { cell: any; row: MRT_Row<any> }) => {
        const { market, slug, hashId } = cell.row.original.operation;
        const navigationId = resolveOperationNavigationId(slug, hashId);
        const childIndent = row.depth > 0 ? 3 : 0;
        const compositionStatus = row.original.ficCompositionStatus as
          | FicCompositionStatus
          | undefined;
        const badgeLabel =
          compositionStatus === 'added' || compositionStatus === 'removed'
            ? ficCompositionStatusLabels[compositionStatus]
            : undefined;
        const badge =
          row.original.isFicChild && badgeLabel ? (
            <Tooltip
              title={
                compositionStatus === 'added'
                  ? 'Adicionado comparado ao mês anterior'
                  : 'Removido comparado ao mês anterior'
              }
              arrow>
              <Chip
                label={badgeLabel}
                size="small"
                variant="outlined"
                sx={
                  compositionStatus === 'added'
                    ? {
                        ml: 1,
                        borderColor: 'success.main',
                        bgcolor: 'success.light',
                        color: 'success.dark',
                      }
                    : {
                        ml: 1,
                        borderColor: 'grey.400',
                        bgcolor: 'grey.100',
                        color: 'grey.700',
                      }
                }
              />
            </Tooltip>
          ) : null;
        const hasNavigationData = Boolean(market && slug && navigationId);
        const isReferenceOnly =
          row.original.isFicCompositionReferenceOnly === true;
        const isUnregisteredFund =
          !row.original.walletFundId || !hasNavigationData;

        if (!hasNavigationData || isReferenceOnly) {
          const operationName = (
            <Box p={1} pl={1 + childIndent} display="flex" alignItems="center">
              {cell.getValue()}
              {badge}
            </Box>
          );

          if (isReferenceOnly || !isUnregisteredFund) {
            return operationName;
          }

          return (
            <Tooltip
              title="Fundo não encontrado na plataforma"
              placement="top"
              arrow>
              {operationName}
            </Tooltip>
          );
        }

        return (
          <Tooltip
            title="Clique para ver a operação"
            placement="top"
            arrow
            sx={{ cursor: 'pointer' }}>
            <Link
              to={`/operacao/${market.toLowerCase()}/${slug}/${navigationId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                textDecoration: 'none',
                color: themev5.palette.text.primary,
              }}>
              <Box
                p={1}
                pl={1 + childIndent}
                display="flex"
                alignItems="center">
                {cell.getValue()}
                {badge}
              </Box>
            </Link>
          </Tooltip>
        );
      },
    }),
    [isFollowedFunds],
  );

  const ficPlColumn: MRT_ColumnDef<any> = useMemo(
    () => ({
      accessorKey: 'ficPlPercentage',
      header: '% PL',
      size: 105,
      minSize: 95,
      maxSize: 130,
      enableSorting: true,
      enableHiding: false,
      enableColumnDragging: false,
      muiTableHeadCellProps: {
        align: 'center',
        sx: {
          textOverflow: 'unset !important',
          overflow: 'visible !important',
          whiteSpace: 'nowrap',
        },
      },
      muiTableBodyCellProps: { align: 'center' },
      accessorFn: (row: any) =>
        resolveFicPlPercentage(
          row.pl_percentage,
          row.vl_merc_pos_final,
          row.vl_patrim_liq,
        ),
      Cell: ({ cell }: { cell: any }) => {
        const value = cell.getValue() as number | 'N/D';
        const marketValue = cell.row.original.vl_merc_pos_final;

        return (
          <Tooltip
            title={formatFicMarketValueInMillions(marketValue)}
            placement="top"
            arrow>
            <Box
              component="span"
              aria-label={`% PL ${formatFicPlPercentage(value)}`}>
              {formatFicPlPercentage(value)}
            </Box>
          </Tooltip>
        );
      },
    }),
    [],
  );

  function getCellValue(
    value: number | string | null | 'N/D',
  ): string | number | null {
    if (value === null) {
      return 'N/A';
    }

    return value;
  }

  const processedColumns = useMemo<MRT_ColumnDef<any>[]>(
    () =>
      filteredColumns?.map((column: MRT_ColumnDef<any>) => {
        switch (column.meta) {
          case 'percentage':
            return {
              ...column,
              size: 140,
              minSize: 120,
              maxSize: 180,
              enableResizing: false,
              muiTableHeadCellProps: {
                sx: {
                  textOverflow: 'unset !important',
                  overflow: 'visible !important',
                  whiteSpace: 'nowrap',
                },
              },
              accessorFn: (row: any) => row[String(column.accessorKey)],
              Cell: ({ cell }: { cell: any }) => {
                const isReferenceOnly =
                  cell.row.original.isFicCompositionReferenceOnly === true;

                return (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      height: '100%',
                      p: 0.5,
                    }}>
                    <IndicatorCellItem
                      value={getCellValue(cell.getValue())}
                      type="percentage"
                      informative={isReferenceOnly}
                      status={
                        isReferenceOnly
                          ? undefined
                          : cell.row.original[`${column.accessorKey}_status`]
                      }
                      criticality={
                        isReferenceOnly
                          ? undefined
                          : cell.row.original[`${column.accessorKey}_alert`]
                      }
                      onClick={
                        isReferenceOnly
                          ? undefined
                          : (e: React.MouseEvent<HTMLDivElement>) => {
                              handleIndicatorClick(
                                cell.row.original.operation,
                                column.header ?? '',
                                column.accessorKey ?? '',
                              );
                            }
                      }
                    />
                  </Box>
                );
              },
            };
          case 'amount':
            return {
              ...column,
              size: 150,
              minSize: 130,
              maxSize: 180,
              enableResizing: false,
              muiTableHeadCellProps: {
                sx: {
                  textOverflow: 'unset !important',
                  overflow: 'visible !important',
                  whiteSpace: 'nowrap',
                },
              },
              accessorFn: (row: any) => row[String(column.accessorKey)],
              Cell: ({ cell }: { cell: any }) => {
                const isReferenceOnly =
                  cell.row.original.isFicCompositionReferenceOnly === true;

                return (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      height: '100%',
                      p: 0.5,
                    }}>
                    <IndicatorCellItem
                      value={getCellValue(cell.getValue())}
                      type="amount"
                      informative={isReferenceOnly}
                      status={
                        isReferenceOnly
                          ? undefined
                          : cell.row.original[`${column.accessorKey}_status`]
                      }
                      criticality={
                        isReferenceOnly
                          ? undefined
                          : cell.row.original[`${column.accessorKey}_alert`]
                      }
                      onClick={
                        isReferenceOnly
                          ? undefined
                          : (e: React.MouseEvent<HTMLDivElement>) => {
                              handleIndicatorClick(
                                cell.row.original.operation,
                                column.header ?? '',
                                column.accessorKey ?? '',
                              );
                            }
                      }
                    />
                  </Box>
                );
              },
            };
          default:
            return column;
        }
      }) || [],
    [filteredColumns, handleIndicatorClick],
  );

  const debouncedSetIndicatorsCompetencyDate = useCallback(
    debounce((value: string) => {
      setIndicatorsCompetencyDate(value);
    }, 500),
    [],
  );

  const handleChangeIndicatorsCompetencyDate = (
    e: React.MouseEvent<HTMLButtonElement>,
    direction: 'previous' | 'next',
  ) => {
    if (isFollowedFunds) {
      handleIndicatorsCompetencyDateChange(e, direction);
      return;
    }

    let formattedCompetenceDate = '';

    const method = {
      previous: subMonths,
      next: addMonths,
    };

    function formattedCompetence(
      date: string,
      callback: (date: number | Date, amount: number) => Date,
    ) {
      return format(startOfMonth(callback(parseISO(date), 1)), 'yyyy-MM-dd');
    }

    formattedCompetenceDate = formattedCompetence(
      competence,
      method[direction],
    );

    setLocalCompetence(formattedCompetenceDate);
    debouncedSetIndicatorsCompetencyDate(formattedCompetenceDate);
  };

  const orderedColumns = useMemo(
    () =>
      !isOperationView && !wallet?.isSharedWallet
        ? [
            alertColumn,
            ...(isFollowedFunds ? [ficPlColumn] : []),
            operationColumn,
            ...(enableFollowActions ? [followedFundActionsColumn] : []),
            ...processedColumns,
          ]
        : [
            ...(isFollowedFunds ? [ficPlColumn] : []),
            operationColumn,
            ...processedColumns,
          ],
    [
      alertColumn,
      ficPlColumn,
      operationColumn,
      processedColumns,
      isOperationView,
      wallet?.isSharedWallet,
      enableFollowActions,
      followedFundActionsColumn,
      isFollowedFunds,
    ],
  );

  const currentColumnKeys = useMemo(
    () => orderedColumns.map((col) => String(col.accessorKey)),
    [orderedColumns],
  );

  const displayCompetence =
    isOperationView && competenceDate ? competenceDate : competence;

  const formattedCompetenceLabel = format(
    startOfMonth(parseISO(displayCompetence)),
    'MMM/yyyy',
    {
      locale: ptBR,
    },
  );

  const handleSelectPreference = useCallback(
    (preference?: TableColumnsPreference) => {
      unsavedChangesRef.current = {};

      if (preference?.data) {
        const { columnOrder: prefOrder, columnVisibility: prefVisibility } =
          preference.data;

        if (prefOrder) {
          const validOrder = prefOrder.filter((col: string) =>
            currentColumnKeys.includes(col),
          );
          const missing = currentColumnKeys.filter(
            (col) => !validOrder.includes(col),
          );
          setLocalColumnOrder([...validOrder, ...missing]);
        }

        if (prefVisibility) {
          const newVisibility = currentColumnKeys.reduce(
            (acc, col) => ({
              ...acc,
              [col]: prefVisibility[col] ?? true,
            }),
            {} as MRT_VisibilityState,
          );
          setLocalColumnVisibility(newVisibility);
        }

        setSelectedTableColumnsPreference(preference);
      } else {
        setLocalColumnOrder(currentColumnKeys);
        setLocalColumnVisibility(
          currentColumnKeys.reduce(
            (acc, col) => ({ ...acc, [col]: true }),
            {} as MRT_VisibilityState,
          ),
        );
        setSelectedTableColumnsPreference(null);
      }
    },
    [currentColumnKeys],
  );

  useEffect(() => {
    try {
      rowVirtualizerInstanceRef.current?.scrollToIndex?.(0);
    } catch (error) {
      //eslint-disable-next-line no-console
      console.error(error);
    }
  }, [sorting]);

  const [localColumnOrder, setLocalColumnOrder] =
    useState<MRT_ColumnOrderState>([]);
  const [localColumnVisibility, setLocalColumnVisibility] =
    useState<MRT_VisibilityState>({});

  const initializedTabRef = useRef<string | null>(null);
  const lastSyncKeyRef = useRef<string>('');

  type TabConfig = {
    columnOrder: MRT_ColumnOrderState;
    columnVisibility: MRT_VisibilityState;
  };
  const unsavedChangesRef = useRef<Record<string, TabConfig>>({});

  const previousValueTypeRef = useRef<string>(valueType);
  useEffect(() => {
    if (
      previousValueTypeRef.current !== valueType &&
      localColumnOrder.length > 0
    ) {
      unsavedChangesRef.current[previousValueTypeRef.current] = {
        columnOrder: localColumnOrder,
        columnVisibility: localColumnVisibility,
      };
    }
    previousValueTypeRef.current = valueType;
  }, [valueType, localColumnOrder, localColumnVisibility]);

  useEffect(() => {
    if (!walletPreferenceData || hasLoadedPreference) return;
    if (currentColumnKeys.length === 0) return;

    if (walletPreferenceData?.[0]?.preference) {
      const preference = walletPreferenceData[0].preference;
      setSelectedTableColumnsPreference(preference);

      if (preference?.data) {
        const { columnOrder: prefOrder, columnVisibility: prefVisibility } =
          preference.data;

        if (prefOrder) {
          const validOrder = prefOrder.filter((col: string) =>
            currentColumnKeys.includes(col),
          );
          const missing = currentColumnKeys.filter(
            (col) => !validOrder.includes(col),
          );
          setLocalColumnOrder([...validOrder, ...missing]);
        } else {
          setLocalColumnOrder(currentColumnKeys);
        }

        const newVisibility = currentColumnKeys.reduce(
          (acc, col) => ({
            ...acc,
            [col]: prefVisibility?.[col] ?? true,
          }),
          {} as MRT_VisibilityState,
        );
        setLocalColumnVisibility(newVisibility);

        initializedTabRef.current = `${valueType}-${currentColumnKeys.length}`;
      }
    } else {
      setSelectedTableColumnsPreference(null);

      setLocalColumnOrder(currentColumnKeys);
      setLocalColumnVisibility(
        currentColumnKeys.reduce(
          (acc, col) => ({ ...acc, [col]: true }),
          {} as MRT_VisibilityState,
        ),
      );
      initializedTabRef.current = `${valueType}-${currentColumnKeys.length}`;
    }

    setHasLoadedPreference(true);
  }, [walletPreferenceData, hasLoadedPreference, currentColumnKeys, valueType]);

  useEffect(() => {
    if (currentColumnKeys.length === 0) return;
    if (!hasLoadedPreference) return;

    const initKey = `${valueType}-${currentColumnKeys.length}`;
    if (initializedTabRef.current === initKey) return;

    const unsavedConfig = unsavedChangesRef.current[valueType];
    if (unsavedConfig && unsavedConfig.columnOrder.length > 0) {
      const validOrder = unsavedConfig.columnOrder.filter((col) =>
        currentColumnKeys.includes(col),
      );
      const missing = currentColumnKeys.filter(
        (col) => !validOrder.includes(col),
      );
      setLocalColumnOrder([...validOrder, ...missing]);

      const newVisibility = currentColumnKeys.reduce(
        (acc, col) => ({
          ...acc,
          [col]: unsavedConfig.columnVisibility[col] ?? true,
        }),
        {} as MRT_VisibilityState,
      );
      setLocalColumnVisibility(newVisibility);
    } else {
      const pref = selectedTableColumnsPreference?.data;
      if (pref) {
        const validOrder = (pref.columnOrder || []).filter((col: string) =>
          currentColumnKeys.includes(col),
        );
        const missing = currentColumnKeys.filter(
          (col) => !validOrder.includes(col),
        );
        setLocalColumnOrder([...validOrder, ...missing]);

        const newVisibility = currentColumnKeys.reduce(
          (acc, col) => ({
            ...acc,
            [col]: pref.columnVisibility?.[col] ?? true,
          }),
          {} as MRT_VisibilityState,
        );
        setLocalColumnVisibility(newVisibility);
      } else {
        setLocalColumnOrder(currentColumnKeys);
        setLocalColumnVisibility(
          currentColumnKeys.reduce(
            (acc, col) => ({ ...acc, [col]: true }),
            {} as MRT_VisibilityState,
          ),
        );
      }
    }

    initializedTabRef.current = initKey;
  }, [
    currentColumnKeys,
    valueType,
    selectedTableColumnsPreference,
    hasLoadedPreference,
  ]);

  useEffect(() => {
    if (localColumnOrder.length === 0 || currentColumnKeys.length === 0) return;

    const syncKey = `${valueType}-${localColumnOrder.join(',')}-${JSON.stringify(localColumnVisibility)}`;
    if (lastSyncKeyRef.current === syncKey) return;
    lastSyncKeyRef.current = syncKey;

    setColumnOrder((prev) => {
      const otherTabColumns = prev.filter(
        (col) => !currentColumnKeys.includes(col),
      );
      return [...otherTabColumns, ...localColumnOrder];
    });

    setColumnVisibility((prev) => {
      const otherTabVisibility: MRT_VisibilityState = {};
      for (const [key, value] of Object.entries(prev)) {
        if (!currentColumnKeys.includes(key)) {
          otherTabVisibility[key] = value;
        }
      }
      return { ...otherTabVisibility, ...localColumnVisibility };
    });
  }, [
    localColumnOrder,
    localColumnVisibility,
    currentColumnKeys,
    valueType,
    setColumnOrder,
    setColumnVisibility,
  ]);

  // O MRT só cria a coluna 'mrt-row-expand' se ela estiver no columnOrder,
  // e a ordem interna dele é congelada no mount (antes dos dados chegarem).
  // Por isso a ordem precisa ser controlada aqui quando existem FICs.
  const effectiveColumnOrder = useMemo(() => {
    if (!hasExpandableRows) {
      return localColumnOrder;
    }

    const baseOrder =
      localColumnOrder.length > 0 ? localColumnOrder : currentColumnKeys;

    return [
      'mrt-row-expand',
      ...baseOrder.filter((columnId) => columnId !== 'mrt-row-expand'),
    ];
  }, [hasExpandableRows, localColumnOrder, currentColumnKeys]);

  const table = useMaterialReactTable({
    columns: orderedColumns,
    data: flattenedData,
    enableExpanding: hasExpandableRows,
    enableExpandAll: false,
    getSubRows: (row) => row.subRows,
    paginateExpandedRows: false,
    filterFromLeafRows: true,
    enableRowVirtualization,
    enableStickyHeader: true,
    enableFullScreenToggle: true,
    enableColumnDragging: false,
    enableColumnOrdering: true,
    enableColumnResizing: true,
    enableHiding: true,
    columnResizeMode: 'onEnd',
    enableColumnActions: false,
    enableEditing: false,
    enableSorting: true,
    enableFilters: true,
    enableGlobalFilter: true,
    enablePagination: true,
    enableBottomToolbar: true,
    enableDensityToggle: false,
    enableColumnFilters: false,
    enableTopToolbar: true,
    rowVirtualizerInstanceRef,
    enableColumnPinning: false,
    enableGlobalFilterModes: true,
    // estimateSize próximo da altura real da linha: quando a medição bate com
    // a estimativa o virtualizador não dispara correções (re-renders extras)
    // ao montar cada linha.
    rowVirtualizerOptions: { overscan: 5, estimateSize: () => 48 },
    onColumnOrderChange: setLocalColumnOrder,
    onColumnVisibilityChange: setLocalColumnVisibility,
    onExpandedChange: setExpanded,
    onSortingChange: setSorting,
    renderTopToolbarCustomActions: (props) => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          width: '35%',
          height: 40,
          color: 'GrayText',
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {!isOperationView && !wallet?.isSharedWallet && (
            <IconButton
              size="small"
              aria-label="Competência anterior"
              onClick={(e) =>
                handleChangeIndicatorsCompetencyDate(e, 'previous')
              }>
              <ChevronLeft />
            </IconButton>
          )}
          <Typography fontSize={14}>Referência:</Typography>
          <Typography fontSize={14} fontWeight={600}>
            {formattedCompetenceLabel}
          </Typography>
          {!isOperationView && !wallet?.isSharedWallet && (
            <IconButton
              size="small"
              aria-label="Próxima competência"
              onClick={(e) => handleChangeIndicatorsCompetencyDate(e, 'next')}>
              <ChevronRight />
            </IconButton>
          )}
        </Box>
      </Box>
    ),
    renderToolbarInternalActions: ({ table }) =>
      isOperationView ? (
        <Box display="flex">
          <MRT_ShowHideColumnsButton
            color="primary"
            title="Personalizar"
            table={table}
            sx={{
              '&:hover': { backgroundColor: 'transparent' },
              '&::after': {
                content: '"Personalizar"',
                color: '#4F4F4F',
                fontSize: 14,
                fontWeight: 600,
                marginLeft: 1,
              },
            }}
          />
          <MRT_ToggleFullScreenButton
            color="primary"
            title="Tela cheia"
            table={table}
            sx={{
              '&:hover': { backgroundColor: 'transparent' },
              '&::after': {
                content: '"Tela cheia"',
                color: '#4F4F4F',
                fontSize: 14,
                fontWeight: 600,
                marginLeft: 1,
              },
            }}
          />
        </Box>
      ) : (
        <ToolbarFundIndicatorsTable
          table={table}
          disabled={false}
          walletId={walletId}
          handleSelectPreference={handleSelectPreference}
          setOpenSavePreferencesModal={setOpenSavePreferencesModal}
          setSelectedTableColumnsPreference={setSelectedTableColumnsPreference}
          selectedTableColumnsPreference={selectedTableColumnsPreference}
        />
      ),
    initialState: {
      density: 'comfortable',
      columnPinning: {
        left: isOperationView
          ? ['mrt-row-expand', 'operationName']
          : [
              'mrt-row-expand',
              'alerta',
              ...(isFollowedFunds ? ['ficPlPercentage'] : []),
              'operationName',
              ...(enableFollowActions ? ['followedFundActions'] : []),
            ],
      },
      pagination: {
        pageSize: 25,
        pageIndex: 0,
      },
    },
    displayColumnDefOptions: {
      'mrt-row-expand': {
        header: '',
        size: 48,
        minSize: 40,
        maxSize: 56,
        muiTableBodyCellProps: {
          sx: { p: 0 },
        },
        Cell: ({ row }: { row: MRT_Row<any> }) => {
          if (!row.getCanExpand()) {
            return null;
          }

          const isExpanded = row.getIsExpanded();

          return (
            <Tooltip
              title={
                isExpanded ? 'Clique para recolher' : 'Clique para expandir'
              }
              placement="top"
              arrow>
              <IconButton
                aria-label={
                  isExpanded ? 'Recolher FIDCs do FIC' : 'Expandir FIDCs do FIC'
                }
                size="small"
                onClick={row.getToggleExpandedHandler()}
                sx={{
                  color: 'primary.main',
                  transition: 'transform 200ms ease',
                  transform: isExpanded ? 'scale(1.1)' : 'scale(1)',
                }}>
                {isExpanded ? <FolderOpen /> : <Folder />}
              </IconButton>
            </Tooltip>
          );
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => {
      const isChildRow = row.depth > 0;
      const isExpandedParent = row.getCanExpand() && row.getIsExpanded();

      if (!isChildRow && !isExpandedParent) {
        return {};
      }

      return {
        sx: {
          ...(isChildRow && {
            // Com virtualização as linhas montam/desmontam durante o scroll,
            // então o fade-in repetido viraria flicker.
            ...(!enableRowVirtualization && {
              animation: 'uqbar-subrow-fade-in 200ms ease-out',
            }),
            '& > td': {
              backgroundColor: '#FFF7F0',
            },
            '&:hover > td': {
              backgroundColor: '#FFF7F0',
            },
          }),
          '& > td:first-of-type': {
            borderLeft: `3px solid ${themev5.palette.primary.main}`,
          },
        },
      };
    },
    muiPaginationProps: {
      color: 'primary',
      shape: 'rounded',
      showRowsPerPage: true,
      variant: 'outlined',
      rowsPerPageOptions: [10, 25, 50, 100],
    },
    localization: {
      ...MRT_Localization_PT_BR,
      noRecordsToDisplay: 'Nenhum resultado encontrado',
    },
    muiTablePaperProps: {
      elevation: 0,
      style: {
        zIndex: 1202,
      },
    },
    muiTableHeadRowProps: {
      sx: (theme) => ({
        backgroundColor: theme.palette.grey[100],
      }),
    },
    muiTableHeadCellProps: {
      align: 'center',
      sx: {
        p: 1,
        px: 1,
        textAlign: 'center',
        whiteSpace: 'nowrap !important',
        overflow: 'visible !important',
        textOverflow: 'unset !important',
        cursor: 'default !important',
        '& *': {
          textOverflow: 'unset !important',
          overflow: 'visible !important',
          whiteSpace: 'nowrap !important',
        },
        '&::after': {
          display: 'none !important',
          visibility: 'hidden !important',
          opacity: '0 !important',
        },
        '&:hover::after': {
          display: 'none !important',
        },
      },
    },
    muiTableBodyCellProps: {
      align: 'center',
      sx: {
        p: 0,
        py: 0.5,
        textAlign: 'center',
        verticalAlign: 'middle',
      },
    },
    muiSearchTextFieldProps: {
      sx: {
        border: 'none',
        padding: '8px',
        '& .MuiInputBase-root': { background: 'transparent' },
        '& .MuiInputBase-input': {
          '&::placeholder': {
            fontSize: '14px',
            color: '#6D6E71',
            fontWeight: 500,
          },
          '&:focus': {
            borderBottom: '2px solid #FF8211',
          },
          borderBottom: '1px solid #E4E5E9',
          fontSize: '14px',
          padding: '0 4px',
        },
        '.MuiSvgIcon-root': { color: '#FF8211', width: '20px', height: '20px' },
        '.MuiInputAdornment-root': { display: 'none' },
      },
      placeholder: 'Buscar',
      variant: 'standard',
    },
    muiTableContainerProps: {
      sx: (theme) => ({
        maxHeight: '600px',
        border: '1px solid #E0E0E0',

        '&::-webkit-scrollbar': {
          height: 10,
          width: 10,
          WebkitAppearance: 'none',
          border: '1px solid #E0E0E0',
        },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: 5,
          backgroundColor: theme.palette.grey['800'],
        },
        userSelect: 'none',
        WebkitUserSelect: 'none',
        KhtmlUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        'o-user-select': 'none',
        '&::selection, &::-moz-selection': {
          background: 'none',
        },
      }),
    },
    muiTopToolbarProps: ({ table }) => ({
      'aria-disabled': false,
      sx: {
        padding: '0 16px',
        backgroundColor: '#FAFAFA',
        boxShadow: '0px 5px 6px 0px rgba(229, 229, 229, 0.50)',
        borderRadius: '3px 3px 0px 0px',
        borderTop: '1px solid #E0E0E0',
        borderRight: '1px solid #E0E0E0',
        borderLeft: '1px solid #E0E0E0',
        '& > .MuiBox-root': {
          justifyContent: table.getState().isFullScreen
            ? 'space-between'
            : 'justify-content',
          alignItems: 'center',
          '& .MuiCollapse-root:first-child': {
            display: 'none',
          },
        },
      },
    }),
    state: {
      isLoading: isLoadingIndicators || isLoadingColumns,
      sorting,
      expanded,
      showProgressBars: isLoadingIndicators || isLoadingColumns,
      showSkeletons: isLoadingIndicators || isLoadingColumns,
      showLoadingOverlay: isLoadingIndicators || isLoadingColumns,

      ...(effectiveColumnOrder.length > 0 && {
        columnOrder: effectiveColumnOrder,
      }),
      ...(Object.keys(localColumnVisibility).length > 0 && {
        columnVisibility: localColumnVisibility,
      }),
    },
  });

  const filterData = useMemo(() => {
    const currentOrder =
      localColumnOrder.length > 0 ? localColumnOrder : currentColumnKeys;
    const currentVisibility =
      Object.keys(localColumnVisibility).length > 0
        ? localColumnVisibility
        : currentColumnKeys.reduce(
            (acc, col) => ({ ...acc, [col]: true }),
            {} as MRT_VisibilityState,
          );

    const mergedOrder = [
      ...columnOrder.filter((col) => !currentColumnKeys.includes(col)),
      ...currentOrder,
    ];

    const mergedVisibility = {
      ...columnVisibility,
      ...currentVisibility,
    };

    return {
      columnOrder: mergedOrder,
      columnVisibility: mergedVisibility,
    };
  }, [
    columnOrder,
    columnVisibility,
    localColumnOrder,
    localColumnVisibility,
    currentColumnKeys,
  ]);

  return (
    <Box px={1}>
      <style>
        {`
          .MuiTableHeadCell-root,
          .MuiTableCell-head,
          .MuiTableHeadCell-root .MuiTypography-root,
          .MuiTableCell-head .MuiTypography-root {
            text-overflow: unset !important;
            overflow: visible !important;
            white-space: nowrap !important;
          }
          
          .MuiTableHeadCell-root *,
          .MuiTableCell-head * {
            text-overflow: unset !important;
            overflow: visible !important;
          }

          @keyframes uqbar-subrow-fade-in {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
        `}
      </style>
      {/* O MRT cria o hook do virtualizador condicionalmente; trocar
          enableRowVirtualization sem remontar violaria a ordem dos hooks. */}
      <MaterialReactTable
        key={enableRowVirtualization ? 'virtualized' : 'standard'}
        table={table}
      />
      {!isOperationView && (
        <SaveTableColumnsPreferencesModal
          open={openSavePreferencesModal}
          handleClose={() => setOpenSavePreferencesModal(false)}
          filterData={filterData}
          walletId={walletId}
          setSelectedPreference={handleSelectPreference}
        />
      )}
    </Box>
  );
}
